import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import agent from '../api/axios';

// Helper to decode JWT payload
function parseJwt<T = any>(token: string): T | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

interface AuthState {
  token: string | null;
  userRole: string | null;
  isAuthenticated: boolean;
  hasActiveSubscription: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  token: null,
  userRole: null,
  isAuthenticated: false,
  hasActiveSubscription: true,
  loading: true,
  error: null,
};

// Async thunk to check subscription status
export const checkSubscription = createAsyncThunk(
  'auth/checkSubscription',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState };
    if (state.auth.userRole === 'Admin') {
      return { hasActiveSubscription: true };
    }

    const subscriptionData = await agent.Business.getMySubscription();
    const isActive =
      subscriptionData.hasSubscription &&
      subscriptionData.status === 'active' &&
      !subscriptionData.cancelAtPeriodEnd;
    return { hasActiveSubscription: isActive };
  }
);

// Async thunk to initialize auth from localStorage
export const initializeAuth = createAsyncThunk('auth/initialize', async () => {
  const storedToken =
    localStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('authToken');

  if (!storedToken) {
    return { token: null, userRole: null };
  }

  const payload = parseJwt<{ exp?: number; role?: string }>(storedToken);
  const { exp, role } = payload ?? {};

  // Check if token is expired
  if (exp && Date.now() >= exp * 1000) {
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    throw new Error('Token expired');
  }

  return { token: storedToken, userRole: role || null };
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<string>) => {
      const token = action.payload;
      state.token = token;
      state.isAuthenticated = true;
      localStorage.setItem('token', token);

      // Parse role from token
      const payload = parseJwt<{ role?: string }>(token);
      state.userRole = payload?.role || null;

      // Admins don't need subscription check
      if (state.userRole === 'Admin') {
        state.hasActiveSubscription = true;
      }
    },
    logout: (state) => {
      state.token = null;
      state.userRole = null;
      state.isAuthenticated = false;
      state.hasActiveSubscription = true;
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
    },
    setSubscriptionStatus: (state, action: PayloadAction<boolean>) => {
      state.hasActiveSubscription = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize auth
      .addCase(initializeAuth.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.token) {
          state.token = action.payload.token;
          state.userRole = action.payload.userRole;
          state.isAuthenticated = true;

          // Admins always have subscription
          if (state.userRole === 'Admin') {
            state.hasActiveSubscription = true;
          }
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.userRole = null;
        state.isAuthenticated = false;
      })
      // Check subscription
      .addCase(checkSubscription.fulfilled, (state, action) => {
        state.hasActiveSubscription = action.payload.hasActiveSubscription;
      })
      .addCase(checkSubscription.rejected, (state) => {
        state.hasActiveSubscription = false;
      });
  },
});

export const { login, logout, setSubscriptionStatus } = authSlice.actions;
export default authSlice.reducer;
